import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Paper,
} from '@mui/material';
import { Add, Refresh, Tune, Check } from '@mui/icons-material';
import { alpha } from '@mui/system';
import styled from '@emotion/styled';
import WidgetRegistry from '../widgets/WidgetRegistry';

const generateWidgetId = () =>
    `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const ToolbarShell = styled(Paper, { shouldForwardProp: (prop) => prop !== 'editing' })(({ theme, editing }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    backgroundColor: editing ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper,
    border: `1px solid ${editing ? alpha(theme.palette.primary.main, 0.4) : 'transparent'}`,
    transition: theme.transitions.create(['background-color', 'border-color'], {
        duration: theme.transitions.duration.short,
    }),
}));

const TopRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const TitleStack = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: '1 1 auto',
});

const Title = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    lineHeight: 1.2,
}));

const Helper = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.smd,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.4,
}));

const PrimaryAction = styled(Button)({
    flexShrink: 0,
    minWidth: 132,
});

const ActionRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

const SecondaryAction = styled(Button)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}));

const WidgetListItem = styled(ListItem)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
}));

const EmptyDialogState = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    textAlign: 'center',
}));

const DashboardToolbar = ({
    onAddWidget,
    onResetLayout,
    isLocked = false,
    onToggleLock,
    userPermissions = [],
    bypassPermissions = false,
    addedWidgetTypes = [],
    title = 'Dashboard',
    showResetLayout = true,
    showLockToggle = true,
}) => {
    const [addWidgetDialogOpen, setAddWidgetDialogOpen] = useState(false);
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

    const editing = !isLocked;

    const handleOpenAddWidget = () => setAddWidgetDialogOpen(true);
    const handleCloseAddWidget = () => setAddWidgetDialogOpen(false);

    const handleOpenResetConfirm = () => setResetConfirmOpen(true);
    const handleCloseResetConfirm = () => setResetConfirmOpen(false);
    const handleConfirmReset = () => {
        onResetLayout?.();
        setResetConfirmOpen(false);
    };

    const handleSelectWidget = (widgetDef) => {
        const widgetId = generateWidgetId();
        const newWidget = {
            i: widgetId,
            x: 0,
            y: Infinity,
            w: widgetDef.defaultSize.w,
            h: widgetDef.defaultSize.h,
            minW: widgetDef.minSize.w,
            minH: widgetDef.minSize.h,
            widgetType: widgetDef.id,
        };
        onAddWidget(newWidget);
        handleCloseAddWidget();
    };

    const permittedWidgets = bypassPermissions
        ? WidgetRegistry.getAll()
        : WidgetRegistry.getAvailableWidgets(userPermissions);
    const addedTypes = new Set(addedWidgetTypes);
    const availableWidgets = permittedWidgets.filter((widget) => !addedTypes.has(widget.id));
    const allAlreadyAdded = permittedWidgets.length > 0 && availableWidgets.length === 0;

    return (
        <>
            <ToolbarShell editing={editing}>
                <TopRow>
                    <TitleStack>
                        <Title variant="h5" component="h1">
                            {editing ? 'Editing dashboard' : title}
                        </Title>
                        {editing && (
                            <Helper>
                                Drag widgets to rearrange · Tap × on a widget to remove
                            </Helper>
                        )}
                    </TitleStack>

                    {showLockToggle && (
                        <PrimaryAction
                            variant="contained"
                            color={editing ? 'success' : 'primary'}
                            startIcon={editing ? <Check /> : <Tune />}
                            onClick={onToggleLock}
                            aria-label={editing ? 'Finish editing dashboard' : 'Customize dashboard'}
                        >
                            {editing ? 'Done' : 'Customize'}
                        </PrimaryAction>
                    )}
                </TopRow>

                {editing && (
                    <ActionRow>
                        <SecondaryAction
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleOpenAddWidget}
                        >
                            Add Widget
                        </SecondaryAction>
                        {showResetLayout && (
                            <SecondaryAction
                                variant="outlined"
                                color="inherit"
                                startIcon={<Refresh />}
                                onClick={handleOpenResetConfirm}
                            >
                                Reset Layout
                            </SecondaryAction>
                        )}
                    </ActionRow>
                )}
            </ToolbarShell>

            <Dialog
                open={addWidgetDialogOpen}
                onClose={handleCloseAddWidget}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Widget</DialogTitle>
                <DialogContent>
                    {availableWidgets.length === 0 ? (
                        <EmptyDialogState>
                            <Typography variant="body2" color="text.secondary">
                                {allAlreadyAdded
                                    ? 'Every widget is already on your dashboard. Remove one to add it back here.'
                                    : 'No widgets available. You may need additional permissions to add widgets.'}
                            </Typography>
                        </EmptyDialogState>
                    ) : (
                        <List>
                            {availableWidgets.map((widget) => {
                                const IconComponent = widget.icon;
                                return (
                                    <WidgetListItem key={widget.id} disablePadding>
                                        <ListItemButton onClick={() => handleSelectWidget(widget)}>
                                            <ListItemIcon>
                                                <IconComponent />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={widget.name}
                                                secondary={widget.description}
                                                secondaryTypographyProps={{ sx: { color: 'text.secondary', fontSize: (theme) => theme.typography.size.md } }}
                                            />
                                        </ListItemButton>
                                    </WidgetListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={resetConfirmOpen}
                onClose={handleCloseResetConfirm}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Reset dashboard?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes every widget from your dashboard. And restores the default layout. You'll need to add back any custom widgets you may have added. This can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseResetConfirm}>Cancel</Button>
                    <Button onClick={handleConfirmReset} variant="contained" color="error">
                        Reset Dashboard
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DashboardToolbar;
